import { Control, Controller, FieldValues, Path, useController } from 'react-hook-form';
import { FormItem, FormLabel } from '@/components/ui/form';
import { Input, InputProps } from '@/components/ui/input';
import { ChangeEvent, useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { PasswordDialog } from './PasswordDialog';
import { useUserStore } from '@/stores/user';
import { Button } from './ui/button';
import { isDisabledDueToTimeout } from '@/utils/helper';
import { SESSION_ENABLE_TIME } from '@/utils/constant';

interface FormFieldComponentProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  required?: boolean;
  rules?: object;
  props?: InputProps;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  isPasswordRequired?: boolean;
  enableFormatting?: boolean;
  formatChar?: 'X' | '*';
}

const FormFieldMaskedInput = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  required,
  rules,
  props,
  className,
  type,
  onChange,
  isPasswordRequired,
  enableFormatting,
  formatChar = 'X',
}: FormFieldComponentProps<T>) => {
  const session = useUserStore((state) => state.validateSession);
  const { field, fieldState } = useController({ name, control });
  const [view, setView] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const maskedValue = (value: string) => {
    if (!value) return '';
    const cleanValue = type === 'number' ? value.replace(/\D/g, '') : value;
    const formattedValue = enableFormatting
      ? cleanValue.length > 5
        ? `${formatChar.repeat(3)}-${formatChar.repeat(2)}-${cleanValue.slice(5)}`
        : cleanValue.length > 3
          ? `${formatChar.repeat(3)}-${cleanValue.slice(3)}`
          : formatChar.repeat(cleanValue.length)
      : formatChar.repeat(cleanValue.length);
    return formattedValue;
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value.length) {
      field.onChange(null);
      return;
    }
    const rawValue = type === 'number' ? e.target.value.replace(/\D/g, '') : e.target.value;
    const formattedValue = enableFormatting
      ? rawValue.length > 5
        ? `${rawValue.slice(0, 3)}-${rawValue.slice(3, 5)}-${rawValue.slice(5)}`
        : rawValue.length > 3
          ? `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`
          : rawValue
      : rawValue;

    if (onChange) {
      onChange({ ...e, target: { ...e.target, value: rawValue } });
    }
    field.onChange(formattedValue);
  };
  const toggleView = () => {
    const currentTime = Date.now();
    if (isPasswordRequired && !view) {
      if ((session?.time && currentTime - session.time > SESSION_ENABLE_TIME) ?? !session) {
        setShowDialog(true);
      } else {
        setView(!view);
      }
    } else {
      setView(!view);
    }
  };

  return (
    <FormItem className="flex flex-col gap-1.5">
      <FormLabel htmlFor={name} className="block text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
      </FormLabel>
      <div
        className={`relative w-full bg-white border-[1px] ${isFocused ? 'border-focuscolor' : 'border-bordercolor'} rounded-md`}
      >
        <Input
          className={`absolute inset-0 px-3 py-2  text-black bg-white rounded-md pointer-events-none text-sm caret-black ${className ?? ''} border-none max-w-[calc(100%-40px)]`}
          value={!view ? maskedValue(field.value as string).trim() : field.value}
          {...props}
          tabIndex={-1}
        />
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={() => (
            <>
              <Input
                type="text"
                id={name}
                value={field.value || ''}
                onChange={handleInput}
                placeholder={placeholder}
                onBlur={() => {
                  setIsFocused(false);
                  field.onBlur();
                }}
                onFocus={() => setIsFocused(true)}
                {...props}
                className={`w-full bg-transparent text-transparent caret-${!field?.value ? 'black' : 'transparent'} outline-none text-sm ${className ?? ''} border-none max-w-[calc(100%-40px)]`}
              />
            </>
          )}
        />
        <Button
          size="icon"
          variant="ghost"
          className="absolute bg-none right-0 flex hover:bg-white items-center -translate-y-1/2 top-1/2 gap-x-1"
          disabled={isDisabledDueToTimeout()}
          tabIndex={-1}
        >
          {view ? (
            <EyeIcon className="cursor-pointer" onClick={toggleView} size={20} />
          ) : (
            <EyeOffIcon className="cursor-pointer" onClick={toggleView} size={20} />
          )}
        </Button>
      </div>
      {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
      {showDialog && (
        <PasswordDialog
          cancelBtnClick={() => setShowDialog(false)}
          confirmBtnClick={() => {
            setShowDialog(false);
            setView(!view);
          }}
        />
      )}
    </FormItem>
  );
};

export default FormFieldMaskedInput;
